import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Registered business information.
 *
 * These are legal-registry values, not copy: they are intentionally kept out of
 * the i18n bundles so there is a single source of truth and no chance of the
 * ko/en files drifting apart. Only the surrounding labels are translated.
 *
 * Required for the NICE 본인확인 (identity verification) service application,
 * which needs a homepage screenshot showing the footer.
 */
const BUSINESS_INFO = {
  companyName: 'MatchGo',
  address: '서울시 중구 을지로5길 19, 24층 에스24030호(수하동, FERRUM TOWER)',
  postalCode: '04539',
  representative: '박세미',
  registrationNumber: '182-86-03623',
  mailOrderRegistrationNumber: '제 2026-서울중구-1331호',
  email: 'matchgo@matchgo.ai',
} as const;

const LEGAL_LINKS = [
  { to: '/legal/terms-of-service', key: 'footer.links.termsOfService' },
  { to: '/legal/privacy-policy', key: 'footer.links.privacyPolicy' },
  { to: '/legal/marketing-consent-collection', key: 'footer.links.marketingConsentCollection' },
  { to: '/legal/marketing-consent-promotion', key: 'footer.links.marketingConsentPromotion' },
] as const;

interface SiteFooterProps {
  /** Adds an `id="footer"` anchor, used by the main page's in-page navigation. */
  anchorId?: boolean;
}

/**
 * Routes that show the footer. Only the main index page does.
 *
 * Matched against `pathname` exactly, so nested routes stay footer-less.
 */
const FOOTER_ROUTES: readonly string[] = ['/'];

/**
 * Footer carrying the registered business information.
 *
 * Mounted from ~15 call sites (`Layout`, `AdminLayout`, the auth screens, the
 * legal pages and the public pages), but it only renders on the main index
 * page — the visibility rule lives here rather than at each call site so a page
 * added later cannot reintroduce the footer by accident. `Layout` and friends
 * keep rendering `<SiteFooter />` unconditionally and this returns null
 * everywhere except `/`.
 *
 * The business registration details are required for the NICE 본인확인 service
 * application, which needs a homepage screenshot showing them, and `/` is the
 * homepage — so that requirement is still satisfied.
 */
export const SiteFooter: React.FC<SiteFooterProps> = ({ anchorId = false }) => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();

  if (!FOOTER_ROUTES.includes(pathname)) return null;

  return (
    <footer
      {...(anchorId ? { id: 'footer' } : {})}
      className="bg-white border-t border-gray-200"
    >
      <div className="max-w-[1200px] mx-auto px-4 py-10">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          {/* Brand */}
          <div className="shrink-0">
            <img
              src="/matchgo_logo_black.png"
              alt="MATCHGO"
              className="h-8 w-auto"
            />
            <p className="mt-3 text-sm text-gray-500">
              {t('mainPage.footer.tagline')}
            </p>
          </div>

          {/* Business registration information */}
          <div className="md:text-right">
            <p className="text-sm font-semibold text-gray-900">
              {BUSINESS_INFO.companyName}
            </p>
            <dl className="mt-2 space-y-1 text-sm leading-relaxed text-gray-600">
              <div className="md:justify-end flex flex-wrap gap-x-1.5">
                <dt className="text-gray-500">{t('footer.business.address')}</dt>
                <dd>
                  {BUSINESS_INFO.address} ({BUSINESS_INFO.postalCode})
                </dd>
              </div>
              <div className="md:justify-end flex flex-wrap gap-x-1.5">
                <dt className="text-gray-500">
                  {t('footer.business.representative')}
                </dt>
                <dd>{BUSINESS_INFO.representative}</dd>
              </div>
              <div className="md:justify-end flex flex-wrap gap-x-1.5">
                <dt className="text-gray-500">
                  {t('footer.business.registrationNumber')}
                </dt>
                <dd>{BUSINESS_INFO.registrationNumber}</dd>
              </div>
              <div className="md:justify-end flex flex-wrap gap-x-1.5">
                <dt className="text-gray-500">
                  {t('footer.business.mailOrderRegistrationNumber')}
                </dt>
                <dd>{BUSINESS_INFO.mailOrderRegistrationNumber}</dd>
              </div>
              <div className="md:justify-end flex flex-wrap gap-x-1.5">
                <dt className="text-gray-500">{t('footer.business.email')}</dt>
                <dd>
                  <a
                    href={`mailto:${BUSINESS_INFO.email}`}
                    className="hover:text-gray-900 hover:underline"
                  >
                    {BUSINESS_INFO.email}
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Legal links + copyright */}
        <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <nav
            aria-label={t('footer.legalNavLabel')}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500"
          >
            {LEGAL_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="hover:text-gray-900 hover:underline"
              >
                {t(link.key)}
              </Link>
            ))}
          </nav>
          <p className="text-xs text-gray-400">
            {t('footer.copyright', {
              year: new Date().getFullYear(),
              company: BUSINESS_INFO.companyName,
            })}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
